'use client';

import { useTranslations } from '@/hooks/useTranslations';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DateRange as DayPickerDateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type IDateRange = {
  from?: Date;
  to?: Date;
};

type DatePickerWithRangeProps = {
  value: IDateRange;
  onChange: (value: IDateRange) => void;
  placeholder?: string;
};

export function DatePickerWithRange({
  value,
  onChange,
  placeholder,
}: DatePickerWithRangeProps) {
  const t = useTranslations('CreateInvoicePopup');

  const label = value?.from
    ? value?.to
      ? `${format(value.from, 'LLL dd, yyyy')} - ${format(value.to, 'LLL dd, yyyy')}`
      : format(value.from, 'LLL dd, yyyy')
    : (placeholder ?? t('dateRange'));

  return (
    <div className={cn('grid gap-2')}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            aria-label="Date range"
            id="service-date"
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal',
              !value?.from && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={value?.from ?? undefined}
            selected={value as DayPickerDateRange}
            onSelect={(range: DayPickerDateRange | undefined) => {
              onChange({
                from: range?.from ?? undefined,
                to: range?.to ?? undefined,
              });
            }}
            numberOfMonths={2}
            className="rounded-lg border shadow-sm"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
